import React, { useState } from 'react';
import { formatDateTime, getStatusColor } from '../../utils/helpers';
import { STATUS_DISPLAY_NAMES } from '../../utils/constants';
import { mapStatusToCategory } from '../../utils/statusCategoryMapper';
import LoadingSpinner from '../common/LoadingSpinner';
import './RecentContainersGrid.css';

const RecentContainersGrid = ({ containers, loading }) => {
  const [filter, setFilter] = useState('ALL');
  const [searchTerm, setSearchTerm] = useState('');

  if (loading) {
    return <LoadingSpinner text="Loading containers..." />;
  }

  // Filter containers
  const filteredContainers = containers.filter(container => {
    const matchesSearch = 
      container.containerNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
      container.bookingNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (container.vesselName && container.vesselName.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesFilter = filter === 'ALL' || mapStatusToCategory(container.status) === filter;
    
    return matchesSearch && matchesFilter;
  });

  const statusOptions = [
    { value: 'ALL', label: 'All Statuses' },
    { value: 'BOOKED', label: 'Booked' },
    { value: 'GATED_IN', label: 'Gated In' },
    { value: 'LOADED', label: 'Loaded' },
    { value: 'IN_TRANSIT', label: 'In Transit' },
    { value: 'DISCHARGED', label: 'Discharged' },
    { value: 'READY_FOR_PICKUP', label: 'Ready for Pickup' },
    { value: 'DELIVERED', label: 'Delivered' }
  ];

  return (
    <div className="recent-containers-grid">
      <div className="grid-header">
        <h2>Recent Containers</h2>
        <p>Browse and manage all containers in the system</p>
      </div>

      <div className="grid-controls">
        <div className="search-control">
          <input
            type="text"
            placeholder="Search containers, bookings, or vessels..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>

        <div className="filter-control">
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="filter-select"
          >
            {statusOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <div className="results-count">
          Showing {filteredContainers.length} of {containers.length} containers
        </div>
      </div>

      {filteredContainers.length > 0 ? (
        <div className="containers-grid">
          {filteredContainers.map(container => (
            <div key={container.id} className="container-card">
              <div className="card-header">
                <div className="container-number">{container.containerNumber}</div>
                <div className={`status-badge ${container.status}`}>
                  {STATUS_DISPLAY_NAMES[container.status] || container.status}
                </div>
              </div>

              <div className="card-content">
                <div className="info-item">
                  <span className="info-label">Booking:</span>
                  <span className="info-value">{container.bookingNumber}</span>
                </div>

                <div className="info-item">
                  <span className="info-label">Type:</span>
                  <span className="info-value">{container.size} {container.type}</span>
                </div>

                <div className="info-item">
                  <span className="info-label">Vessel:</span>
                  <span className="info-value">{container.vesselName || 'Not assigned'}</span>
                </div>

                <div className="info-item">
                  <span className="info-label">Voyage:</span>
                  <span className="info-value">{container.voyageNumber || 'N/A'}</span>
                </div>

                <div className="info-item">
                  <span className="info-label">Route:</span>
                  <span className="info-value route">
                    {container.portOfLoading} → {container.portOfDischarge}
                  </span>
                </div>

                <div className="info-item">
                  <span className="info-label">Shipping Line:</span>
                  <span className="info-value">{container.shippingLine || 'N/A'}</span>
                </div>
              </div>

              <div className="card-footer">
                <div className="timestamp">
                  <span className="timestamp-label">Last Updated:</span>
                  <span className="timestamp-value">{formatDateTime(container.updatedAt)}</span>
                </div>
                {container.eta && (
                  <div className="timestamp">
                    <span className="timestamp-label">ETA:</span>
                    <span className="timestamp-value">{formatDateTime(container.eta)}</span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="empty-state">
          <div className="empty-icon">📭</div>
          <h3>No Containers Found</h3>
          <p>No containers match your current filters</p>
        </div>
      )}
    </div>
  );
};

export default RecentContainersGrid;